<template>
    <div class="forms-page edit-form-page">
        <!-- Modern Header -->
        <div class="forms-header">
            <div class="header-left">
                <h1 class="page-title">Edit Form</h1>
                <p class="page-subtitle">Modify and update your form configuration</p>
            </div>
            <div class="header-actions">
                <button
                    v-for="lang in enabledTranslationLanguages"
                    :key="lang.code"
                    type="button"
                    class="btn-preview-language"
                    @click="previewInLanguage(lang.code)"
                    :disabled="loading || !formData"
                >
                    <i class="fa fa-language"></i>
                    <span> Preview in {{ lang.name }}</span>
                </button>
                <button
                    v-if="aiFormsEnabled"
                    type="button"
                    class="btn-ai-edit"
                    @click="openAiEdit"
                    :disabled="loading || !formData"
                >
                    <i class="fa fa-magic"></i>
                    <span> Edit with AI</span>
                </button>
                <button
                    v-else-if="aiFormsComingSoon"
                    data-test="ai-forms-edit-coming-soon"
                    type="button"
                    class="btn-ai-edit"
                    title="Edit with AI is coming soon"
                    disabled
                >
                    <i class="fa fa-magic"></i>
                    <span> Edit with AI</span>
                    <span class="badge badge-light ml-1">Coming soon</span>
                </button>
                <button
                    type="button"
                    class="btn-save"
                    @click="_saveForm"
                    :disabled="loading || !formData || !formName || formName.trim() === ''"
                >
                    <i class="fa fa-save"></i>
                    <span> Save Changes</span>
                </button>
            </div>
        </div>

        <ai-edit-form-modal v-if="aiFormsEnabled" ref="aiEditModal" :site-id="siteID" :config="formData" @apply="onAiEditApply" />

        <ErrorDisplay/>
        <CommonOverlay :show="loading" :title="overlayTitle" :message="overlayMessage" />

        <ai-setup-summary :report="aiSetupReport" />

        <!-- Form Configuration Card -->
        <div class="config-card">
            <div class="config-card-header">
                <h3 class="config-title">
                    <i class="fa fa-cog"></i>
                    Form Configuration
                </h3>
            </div>
            <div class="config-card-body">
                <!-- Form Details Row -->
                <div class="config-row details-row">
                    <div class="config-field">
                        <label class="field-label">Form Name <span class="required">*</span></label>
                        <input
                            v-model="formName"
                            type="text"
                            class="modern-input"
                            :class="{ 'input-error': !formName || formName.trim() === '' }"
                            placeholder="Enter form name..."
                            :disabled="!formData"
                        />
                        <small v-if="!formName || formName.trim() === ''" class="field-error">
                            Form name is required
                        </small>
                    </div>
                    <div class="config-field">
                        <label class="field-label">Form Type</label>
                        <div class="form-type-display">
                            <i class="fa fa-tag"></i>
                            <span>{{ formType || 'Not specified' }}</span>
                        </div>
                    </div>
                </div>

                <!-- Skip Validation Option -->
                <div class="config-row">
                    <div class="skip-validation-option">
                        <label class="skip-validation-toggle">
                            <input
                                type="checkbox"
                                v-model="skipValidation"
                                :disabled="!formData"
                            />
                            <span class="toggle-label">Skip patient verification (DOB & Initials)</span>
                        </label>
                        <p class="skip-validation-hint">
                            When enabled, patients will be taken directly to the form without verifying their Date of Birth and Initials.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Form Builder Section -->
        <div class="builder-section">
            <FormBuilder v-if="formData" v-model="formData" :siteID="siteID" :formType="formType"></FormBuilder>
            <!-- Loading State - only show if not already showing global loading spinner -->
            <div v-else-if="!loading" class="loading-state">
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading form...</span>
            </div>
        </div>
    </div>
</template>

<script>
    /**
     * NOTE - This file only exists for development purpose.
     */
    import FormBuilder from "./FormBuilder";
    import FormRenderer from "./FormRenderer";
    import { mapState, mapActions, mapMutations } from 'vuex';
    import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
    import CommonOverlay from "@/components/CommonOverlay";
    import Multiselect from "vue-multiselect";
    import moment from 'moment-timezone';
    import { langSuffix, LANGUAGE_CONFIG } from '../utils/languageHelper';
    import { sanitizeFormConfig } from '../libraries/sanitize-config';
    import { getMissingOdImagingFields } from '../libraries/validate-od-imaging-config';
    import { getFormOdUniqueMappingError } from '../libraries/validate-od-unique-mapping';
    import { getFormOdCategorizedOtherProblemsError } from '../libraries/od-categorized-diseases';
    import { EVENT_CONSTANTS } from '../configs/events';
    import { CONTROLS } from '../configs/controls';
    import AiEditFormModal from './AiEditFormModal.vue';
    import AiSetupSummary from './AiSetupSummary.vue';

    import "../css/bootstrap-4.3.1.min.css";
    import "../css/v-form-builder.css";

    export default {
        name: "EditForm",
        props: ['formID'],
        components: {
            FormBuilder,
            FormRenderer,
            ErrorDisplay,
            Multiselect,
            CommonOverlay,
            AiEditFormModal,
            AiSetupSummary
        },

        created() {
            this.init();
            this.$formEvent.$on("submit", async(value) => {
                //console.dir(this.formInputData);
                if(!this.formID) {
                    this.$toast.error('Somethng is not right. Please try refreshing and submitting the form again');
                    return;
                }

                if(!this.siteID) {
                    this.$toast.error('SiteID is missing. Please try refreshing and submitting the form again');
                    return;
                }
                await this._saveFormSubmission();
            });

            // Listen for auto-save after translation
            this.$formEvent.$on('saveFormAfterTranslation', async () => {
                console.log('Auto-saving form after translation...');
                await this._saveForm();
                this.$toast.success('Form translated and saved successfully!');
            });
        },

        beforeDestroy() {
            // Clean up event listeners
            this.$formEvent.$off('saveFormAfterTranslation');
        },

        data: () => ({
            siteID: null,
            forms: [],
            formCategories: [],
            formOptions: [],            
            selectedForm: [],            
            formName: "",
            formData: null,
            formType: null,
            skipValidation: false,  // Skip DOB & Initials verification - default false for backward compatibility
            isShowDevNote: false,
            isRenderer: false,
            formInputData: null,
            isShowData: false,
            readOnly: false,
            aiSetupReport: null,   // AI edit "what I configured / what needs attention" summary
        }),

        methods: {
            ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
            ...mapActions('forms', ['getForm','getForms', 'saveForm', 'saveFormSubmission', 'translateFormConfig', 'editForm']),

            openAiEdit() {
                this.$refs.aiEditModal.show();
            },

            /**
             * Apply an AI edit returned by AiEditFormModal. The backend already preserved
             * all ids and the conditional-logic graph; run it through sanitizeFormConfig
             * (the same path used when loading a form) for shape consistency, then swap
             * it into the builder. Not saved until the user clicks Save Changes.
             */
            onAiEditApply(editedConfig, setupReport) {
                const { sanitized } = sanitizeFormConfig(editedConfig, { controlDefinitions: CONTROLS });
                this.formData = sanitized;
                if (setupReport) this.aiSetupReport = setupReport;
            },

            async init() {
                // Validate formID prop first
                if (!this.formID) {
                    console.error('EditForm: No formID provided as prop');
                    this.$toast.error('Form ID is missing. Cannot edit form.');
                    return;
                }

                let res = await this.getForm({formIDForWeb: this.formID});
                
                if(res.data) {
                    this.siteID = res.data.config.SiteID;
                    const { sanitized: initConfig } = sanitizeFormConfig(JSON.parse(res.data.config.ConfigData), { controlDefinitions: CONTROLS });
                    this.formData = Object.assign({}, initConfig);
                    this.formName = res.data.config.Name;
                    this.formType = res.data.config.FormType;
                    // Load skipValidation - treat NULL/undefined as false for backward compatibility with existing forms
                    this.skipValidation = res.data.config.SkipValidation === 1 || res.data.config.SkipValidation === true;
                    this.formData.formConfig.pdfCategories = res.data.pdfCategories;
                    // Store formIDForWeb in formConfig so sidebar components can access it
                    this.formData.formConfig.formIDForWeb = this.formID;
                }
                else {
                    console.error(`EditForm: Failed to load form with ID ${this.formID}:`, res);
                    this.$toast.error('Failed to load form data. Please try again.');
                }
            },

            formsCustomLabel({Name, FormIDForWeb}) {
                return `${Name}`;
            },

            getData() {
                console.log(JSON.stringify(this.formData))
            },

            createBlank() {
                this.formData = Object.assign({})
            },

            setData() {
                //this.formData = Object.assign({}, DEMO_FORM_DATA);
            },

            viewRenderer() {
                if (!this.isRenderer) {
                    this.setData();
                    this.isRenderer = true;
                    return;
                }

                this.isRenderer = false;
            },

            setReadonly() {
                if (this.readOnly) {
                    this.readOnly = false;
                    return;
                }

                this.setRandomData();
                this.readOnly = true;
            },

            async _getForms() {
                /*
                let res = await this.getForms({siteID: this.siteID});
                if(res && res.data.length > 0) {
                    // add label and track-by property because it's required by multiselect
                    //res.data.map(form => ({ ...form, label: form.Name, `track-by`: form.FormIDForWeb }));
                    this.formOptions = res.data.map(({ Name }) => Name);
                    this.forms = res.data;
                    //console.dir(forms);                    
                }
                */

                let res = await this.getForms({siteID: this.siteID});
                if(res && res.data.length > 0) {                    
                    // add label and track-by property because it's required by multiselect
                    //res.data.map(form => ({ ...form, label: form.Name, `track-by`: form.FormIDForWeb }));                    
                    this.forms = res.forms;
                    this.formCategories = res.categories;                    
                    this.formOptions = this.forms.map(({ Name }) => Name);
                    //console.dir(this.forms); 
                }
                
            },

            loadForm() {                
                const { sanitized: selectedConfig } = sanitizeFormConfig(JSON.parse(this.selectedForm.ConfigData), { controlDefinitions: CONTROLS });
                this.formData = selectedConfig;
                this.formName = this.selectedForm.Name;
            },

            /**
             * Open form preview in a specific language in a new tab
             */
            previewInLanguage(langCode) {
                if (!this.formID) {
                    this.$toast.error('Form ID is missing');
                    return;
                }
                const routeData = this.$router.resolve({
                    path: `/preview-form/${this.formID}`,
                    query: { lang: langCode }
                });
                window.open(routeData.href, '_blank');
            },

            async _saveForm() {
                // Critical validation checks to prevent duplication
                if (!this.formID) {
                    console.error('EditForm: Cannot save - formID is missing');
                    this.$toast.error('Cannot save form: Form ID is missing. Please refresh and try again.');
                    return;
                }

                if (!this.siteID) {
                    console.error('EditForm: Cannot save - siteID is missing');
                    this.$toast.error('Cannot save form: Site ID is missing. Please refresh and try again.');
                    return;
                }

                if (!this.formName || this.formName.trim() === '') {
                    console.error('EditForm: Cannot save - form name is empty');
                    this.$toast.error('Please enter a name for this form before saving.');
                    return;
                }

                if (!this.formData) {
                    console.error('EditForm: Cannot save - form data is missing');
                    this.$toast.error('Cannot save form: Form configuration is missing. Please refresh and try again.');
                    return;
                }

                const missingOdImagingFields = getMissingOdImagingFields(this.formData.formConfig);
                if (missingOdImagingFields.length > 0) {
                    this.$toast.error(`Cannot save form: please set ${missingOdImagingFields.join(' and ')} in Form Configurations. Forms without these settings will not save to Open Dental when patients submit them.`, { timeout: 9000 });
                    this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.SIDEBAR.OPEN, 'FormConfiguration');
                    return;
                }

                // Two controls on one Open Dental list column join with '\n' at submit time and
                // wipe the patient's problems / allergies / medications. Refuse the save.
                const duplicateOdMappingError = getFormOdUniqueMappingError(this.formData);
                if (duplicateOdMappingError) {
                    this.$toast.error(duplicateOdMappingError, { timeout: 12000 });
                    return;
                }

                // A categorized diseases field saved to Diseases writes Other Problems itself; a
                // second writer would be concatenated into the same note. Refuse the save.
                const otherProblemsError = getFormOdCategorizedOtherProblemsError(this.formData);
                if (otherProblemsError) {
                    this.$toast.error(otherProblemsError, { timeout: 12000 });
                    return;
                }

                let requiredODFields = ['FName', 'LName', 'BirthDate'];
                                
                for (const key of Object.keys(this.formData.controls)) {
                    let temp = this.formData.controls[key];
                    // let's make sure BirthDate is never empty because it's required for authenticating form
                    if(requiredODFields.includes(temp.teamioODColumn)) {
                        let controlName = temp.name;
                        let validation = {
                            additionalValue: '',
                            errorMessage: `${temp.label} is a required field`,
                            ruleType: 'required'
                        }

                        if(this.formData.controls[key].validations && this.formData.controls[key].validations.length > 0) {
                            // check if required is not already in there
                            let required = this.formData.controls[key].validations.filter(a => a.ruleType === 'required');
                            if(required && required.length === 0)
                                this.formData.controls[key].validations.push(validation);
                        }
                        else
                            this.formData.controls[key].validations = [validation];
                    }
                }

                // Auto-translate form controls for all enabled languages
                const targetLanguages = this.enabledTranslationLanguages;
                for (const lang of targetLanguages) {
                    try {
                        console.log(`Translating form to ${lang.name} (${lang.code})...`);
                        this.$store.dispatch('alert/setLoading', true);

                        const translateResponse = await this.translateFormConfig({
                            formIDForWeb: this.formID,
                            targetLanguage: lang.code,
                            forceRetranslate: false
                        });

                        if (translateResponse && translateResponse.data && translateResponse.data.translatedConfigData) {
                            // CRITICAL: Only merge translation fields, NOT the entire formData
                            // The translate endpoint fetches from DB which has stale data (user's edits not yet saved)
                            const translatedData = translateResponse.data.translatedConfigData;
                            const suffix = langSuffix(lang.code);

                            // Merge translated fields into existing controls (preserve user's edits)
                            if (translatedData.controls && this.formData.controls) {
                                for (const [controlId, translatedControl] of Object.entries(translatedData.controls)) {
                                    if (this.formData.controls[controlId]) {
                                        for (const [key, value] of Object.entries(translatedControl)) {
                                            if (key.endsWith(suffix)) {
                                                this.formData.controls[controlId][key] = value;
                                            }
                                        }
                                    }
                                }
                            }

                            // Merge translated fields into existing sections
                            if (translatedData.sections && this.formData.sections) {
                                for (const [sectionId, translatedSection] of Object.entries(translatedData.sections)) {
                                    if (this.formData.sections[sectionId]) {
                                        for (const [key, value] of Object.entries(translatedSection)) {
                                            if (key.endsWith(suffix)) {
                                                this.formData.sections[sectionId][key] = value;
                                            }
                                        }
                                    }
                                }
                            }

                            // Merge translated fields into formConfig
                            if (translatedData.formConfig && this.formData.formConfig) {
                                for (const [key, value] of Object.entries(translatedData.formConfig)) {
                                    if (key.endsWith(suffix)) {
                                        this.formData.formConfig[key] = value;
                                    }
                                }
                            }

                            console.log(`Auto-translation to ${lang.name} completed. Stats:`, translateResponse.data.stats);
                        }
                    } catch (translateError) {
                        console.warn(`Auto-translation to ${lang.name} failed, continuing:`, translateError);
                    }
                }

                let payload = {
                    formIDForWeb: this.formID, // This should NEVER be null/undefined for EditForm
                    siteID: this.siteID,
                    name: this.formName.trim(),
                    formData: JSON.stringify(this.formData),
                    formType: this.formType,
                    skipValidation: this.skipValidation,  // Include skip validation setting
                    dateUpdated: moment().format('YYYY-MM-DD HH:mma')
                };

                console.log('✏️ EditForm: About to save form with payload:', {
                    formIDForWeb: payload.formIDForWeb,
                    name: payload.name,
                    formDataControls: Object.keys(this.formData.controls).length,
                    firstControl: Object.keys(this.formData.controls)[0] ? {
                        name: this.formData.controls[Object.keys(this.formData.controls)[0]].name,
                        label: this.formData.controls[Object.keys(this.formData.controls)[0]].label
                    } : null
                });

                try {
                        this.$store.dispatch('alert/setLoading', true);
                        const saveResponse = await this.saveForm(payload);
                        this.$toast.success('Form updated successfully!');
                        
                        // CRITICAL FIX (Jan 21, 2026): Reload form from database after successful save
                        // This ensures frontend state matches what was actually persisted to the database
                        // Without this, edited fields would revert because stale formData stays in memory
                        console.log('Reloading form data from database after successful save...');
                        const refreshedForm = await this.getForm({formIDForWeb: this.formID});
                        if(refreshedForm && refreshedForm.data) {
                            const { sanitized: refreshedConfig } = sanitizeFormConfig(JSON.parse(refreshedForm.data.config.ConfigData), { controlDefinitions: CONTROLS });
                            this.formData = Object.assign({}, refreshedConfig);
                            this.formName = refreshedForm.data.config.Name;
                            this.formType = refreshedForm.data.config.FormType;
                            this.skipValidation = refreshedForm.data.config.SkipValidation === 1 || refreshedForm.data.config.SkipValidation === true;
                            console.log('✅ Form reloaded from database - frontend and database now in sync');
                        }
                } catch (error) {
                    console.error('EditForm: Save operation failed:', error);
                    const serverMsg = error && error.response && typeof error.response.data === 'string' && error.response.data;
                    this.$toast.error(serverMsg || `Failed to save form: ${error.message}`);
                }
                    finally {
                        this.$store.dispatch('alert/setLoading', false);
                    }
            },
        },

        computed: {
            /**
             * Get list of enabled translation languages (non-English) for this form
             */
            enabledTranslationLanguages() {
                if (!this.formData || !this.formData.formConfig) return [];
                const supported = this.formData.formConfig.supportedLanguages || [];
                return supported
                    .filter(code => code !== 'en' && LANGUAGE_CONFIG[code])
                    .map(code => ({ code, name: LANGUAGE_CONFIG[code].name }));
            },

            ...mapState({
                loading: state => state.alert.loading,
                loaded: state => state.alert.loaded,
                errored: state => state.alert.errored,
                alertMessage: state => state.alert.message,
            })
            ,
            overlayTitle() {
                if (!this.formData) return 'Loading form';
                return this.loading ? 'Saving form' : '';
            },
            overlayMessage() {
                if (!this.formData) return 'Please wait while we load the form...';
                return this.loading ? 'Please wait while we save the form...' : '';
            },
            // AI form builder is an optional paid add-on gated per-site by the
            // AI_FORMS_PAID_FOR setting. This component doesn't use the global mixin,
            // so read the setting straight from the customer store (same pattern as
            // PatientProfile's local formsPaidFor). Hides "Edit with AI" when off.
            aiFormsEnabled() {
                const settings = (this.$store.state.customer && this.$store.state.customer.settings) || [];
                const s = settings.find(x => x && x.Property === 'AI_FORMS_PAID_FOR');
                return !!(s && s.PropertyValue === 'TRUE');
            },
            // AI features aren't launched (2026-09-16): unentitled → disabled "Coming soon" button.
            // Waits for settings so a paying site never flashes it.
            aiFormsComingSoon() {
                const settings = (this.$store.state.customer && this.$store.state.customer.settings) || [];
                return settings.length > 0 && !this.aiFormsEnabled;
            }
        }
    }
</script>

<!-- New step!
     Add Multiselect CSS. Can be added as a static asset or inside a component. -->
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
// Modern Forms Page Styling - matching Admin Dashboard
.forms-page.edit-form-page {
    font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
    color: #334155;
    background-color: #f8fafc;
    padding: 1.5rem 2rem;
    min-height: 100vh;

    @media (max-width: 768px) {
        padding: 1rem;
    }
}

// Header
.forms-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .header-left {
        flex: 1;
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
}

.page-title {
    font-size: 1.75rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 4px 0;
    line-height: 1.3;
}

.page-subtitle {
    font-size: 0.9375rem;
    color: #64748b;
    margin: 0;
}

// Save Button
.btn-save {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: #3abf94;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: darken(#3abf94, 8%);
        transform: translateY(-1px);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    i {
        font-size: 0.875rem;
    }
}

// Edit with AI Button
.btn-ai-edit {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: #6366f1;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: darken(#6366f1, 8%);
        transform: translateY(-1px);
    }

    &:disabled { opacity: 0.6; cursor: not-allowed; }
    i { font-size: 0.875rem; }
}

// Preview in Language Button
.btn-preview-language {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: #6366f1;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background-color: darken(#6366f1, 8%);
        transform: translateY(-1px);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    i {
        font-size: 0.875rem;
    }
}

// Configuration Card
.config-card {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
    margin-bottom: 24px;
    overflow: hidden;
}

.config-card-header {
    padding: 16px 20px;
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.config-title {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
        color: #64748b;
        font-size: 1rem;
    }
}

.config-card-body {
    padding: 20px;
}

.config-row {
    margin-bottom: 20px;

    &:last-child {
        margin-bottom: 0;
    }
}

.details-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.config-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #475569;
    margin: 0;

    .required {
        color: #ef4444;
    }
}

.modern-input {
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #334155;
    background-color: white;
    transition: all 0.2s ease;

    &::placeholder {
        color: #94a3b8;
    }

    &:focus {
        outline: none;
        border-color: #3abf94;
        box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
    }

    &.input-error {
        border-color: #ef4444;

        &:focus {
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
    }

    &:disabled {
        background-color: #f8fafc;
        color: #94a3b8;
        cursor: not-allowed;
    }
}

.field-error {
    color: #ef4444;
    font-size: 0.75rem;
    margin-top: -4px;
}

// Form Type Display
.form-type-display {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #64748b;

    i {
        color: #94a3b8;
    }

    span {
        color: #334155;
        font-weight: 500;
    }
}

// Skip Validation Option
.skip-validation-option {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
}

.skip-validation-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-bottom: 0;
    gap: 12px;

    input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: #3abf94;
        border-radius: 4px;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    .toggle-label {
        font-weight: 600;
        font-size: 0.875rem;
        color: #334155;
    }
}

.skip-validation-hint {
    margin: 10px 0 0 30px;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
}

// Builder Section
.builder-section {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
    overflow: hidden;
    min-height: 400px;
}

// Loading State
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px 20px;
    color: #64748b;
    font-size: 0.9375rem;

    i {
        font-size: 1.25rem;
        color: #3abf94;
    }
}
</style>

<style src="./formstyles.scss" lang="scss" />

